/**
 * SOLID Review
 * S — Strong. One concern: the frontend Application's boundary — its sockets, its lollipops, and the
 *     one build step that turns connected sockets into the context modules behind the lollipops.
 * O — Good. A new context module is one more field of `IEntityApplicationContexts`, built in `build()`.
 * L — N/A — no inheritance hierarchy.
 * I — Strong. Two holders and a build step; the modules are private, reached only through the lollipops.
 * D — Strong. The two holders and the module classes; every collaborator arrives through a socket.
 */
import { ComponentFrontendApplicationRequiredPorts } from './ComponentFrontendApplicationRequiredPorts';
import { ComponentFrontendApplicationProvidedPorts } from './ComponentFrontendApplicationProvidedPorts';
import type { IEntityApplicationContexts } from './ComponentFrontendApplicationTypes';
import { TableContext, type IConfigDepTableContext } from '../table/TableContext';
import { SessionContext, type IConfigDepSessionContext } from '../session/SessionContext';
import { AuthContext, type IConfigDepAuthContext } from '../auth/AuthContext';
import { LegalContext, type IConfigDepLegalContext } from '../legal/LegalContext';
import { PaymentContext, type IConfigDepPaymentContext } from '../payment/PaymentContext';
import { AdsContext, type IConfigDepAdsContext } from '../ads/AdsContext';
import { HealthContext, type IConfigDepHealthContext } from '../health/HealthContext';
import { MediaContext, type IConfigDepMediaContext } from '../media/MediaContext';
import { DiagnosticsContext, type IConfigDepDiagnosticsContext } from '../diagnostics/DiagnosticsContext';
import { ClientSteps, type IConfigDepClientSteps } from '../shared/ClientSteps';
import { ServerEvents, type TServerEventHandlers } from '../shared/ServerEvents';

/**
 * THE FRONTEND APPLICATION AS A COMPONENT (frontend strict-ports plan, FE1, 2026-10-03), shaped exactly
 * like the backend's `ComponentApplication`: `MODEL_NAME`, two holder classes, and the three-phase
 * lifecycle — construct, then the composition root connects every socket, then `build()` reads them.
 *
 * The phases exist for the backend's reason. The provided holder must exist at construction, because
 * `describePorts` walks fields and Infrastructure's socket is connected to it. The context modules behind it
 * need the sockets connected first. So the lollipops forward at CALL time to what `build()` assigns.
 *
 * THE MODULES ARE PRIVATE (frontend-application-oo P7, 2026-10-28 — the backend's application-oo A5): nine context modules,
 * each holding its use cases by role, and the dispatcher of the server's deliveries. Until P7 each was a public getter of
 * this class; now the lollipops are handed a supplier of them, and nothing outside the box reaches a use case but through
 * `ClientUseCases` or `ClientServerEvents`.
 */
export class ComponentFrontendApplication {
  /** The model name in every generated diagram — one literal, so a typo cannot split this box in two. */
  static readonly MODEL_NAME = 'ComponentFrontendApplication';

  readonly requiredPorts = new ComponentFrontendApplicationRequiredPorts();
  /** Assigned in the constructor body: the holder takes a supplier of the modules. */
  readonly providedPorts: ComponentFrontendApplicationProvidedPorts;

  /** What `build()` makes of the sockets; null until then. */
  private contexts: IEntityApplicationContexts | null = null;

  constructor() {
    this.providedPorts = new ComponentFrontendApplicationProvidedPorts(() => this.builtContexts());
  }

  /**
   * Phase 3: read every socket — after assembly, so each resolves to its connected provider rather than
   * its fallback, and `assertAllPortsRead` can see that each one was read — and build the context modules once.
   */
  build(): void {
    const sockets = this.requiredPorts;
    const decisions = sockets.clientDecisionsSocket.getInterface();
    const state = sockets.clientStateSocket.getInterface();
    const rounds = sockets.clientRoundServerSocket.getInterface();
    const rooms = sockets.clientRoomServerSocket.getInterface();
    const tabStorage = sockets.clientTabStorageSocket.getInterface();
    // ONE serial section for every module: they write the same stores (FE2).
    const stepsDeps: IConfigDepClientSteps = { state, tabStorage };
    const steps = new ClientSteps(stepsDeps);
    const watch = sockets.clientServerWatchSocket.getInterface();
    const tableDeps: IConfigDepTableContext = { decisions, state, rounds, rooms, watch, steps };
    const table = new TableContext(tableDeps);
    const auth = sockets.clientAuthServerSocket.getInterface();
    const authDeps: IConfigDepAuthContext = { decisions, auth, steps };
    const accountSide = new AuthContext(authDeps);
    // The account's sign-out, which the session (no player behind the token) and the legal side (a deleted account) sign out with.
    const account = accountSide.signOut;
    const sessionDeps: IConfigDepSessionContext = {
      decisions, tabStorage, rooms, rounds, steps, auth, account,
      players: sockets.clientPlayerServerSocket.getInterface(),
    };
    const legalDeps: IConfigDepLegalContext = {
      decisions, steps, account,
      legal: sockets.clientLegalServerSocket.getInterface(),
      consent: sockets.clientConsentPlatformSocket.getInterface(),
    };
    const legal = new LegalContext(legalDeps);
    const paymentDeps: IConfigDepPaymentContext = { decisions, steps, watch, payments: sockets.clientPaymentServerSocket.getInterface() };
    const payment = new PaymentContext(paymentDeps);
    const adsDeps: IConfigDepAdsContext = { decisions, steps, ads: sockets.clientAdsServerSocket.getInterface() };
    const healthDeps: IConfigDepHealthContext = { health: sockets.clientServerHealthSocket.getInterface() };
    const mediaDeps: IConfigDepMediaContext = { decisions, steps, media: sockets.clientMediaServerSocket.getInterface() };
    const diagnosticsDeps: IConfigDepDiagnosticsContext = { decisions, steps, diagnostics: sockets.clientDiagnosticsServerSocket.getInterface() };
    // One delivery seam, three takers: the consent platform's changes to the legal side (FE4b), the account's pushed credits
    // to the payment side (FE4c), the rest to the table.
    const handlers: TServerEventHandlers = { ...table.events, consentChanged: legal.consentChanged, creditsUpdated: payment.creditsUpdated };
    const contexts: IEntityApplicationContexts = {
      table, session: new SessionContext(sessionDeps), auth: accountSide, legal, payment, ads: new AdsContext(adsDeps),
      health: new HealthContext(healthDeps), media: new MediaContext(mediaDeps), diagnostics: new DiagnosticsContext(diagnosticsDeps),
      events: new ServerEvents(handlers),
    };
    this.contexts = contexts;
  }

  /** The modules the lollipops forward to; asked before `build()` — a composition-root defect — it says so rather than reading a null. */
  private builtContexts(): IEntityApplicationContexts {
    if (this.contexts === null) {
      throw new Error('ComponentFrontendApplication: a use case was called before build() — assemble the graph, then build.');
    }
    return this.contexts;
  }
}
